// The payload a virtual-question partial draws, ported from the coffee partial's
// renderPartialModel. A virtual question is drawn by whichever question block its variant names,
// so the payload is that block's own shape -- what differs between the four is worked out here.
//
// What a pattern addresses is resolved upstream, against the survey and report registries, and
// the answer variants it yields are handed in as `subItemAnswerVariants`.

import { getImage } from '@aytm/app/stats/lib/stat-page'

const withImage = av => {
  const image = getImage(av.image) || null

  return null == image ? { ...av } : { ...av, image }
}

// An extended open end draws its variants as sub-items rather than as a chart or a table of its
// own, and a lone variant has no list to expand.
const calcOexData = answerVariants => ({
  components: {
    chart: false,
    table: false,
    subitems: { table: true, expand: 1 < answerVariants.length }
  },
  answerVariants: answerVariants.map(av => {
    const answerVariant = { ...withImage(av), positive: true }

    if (1 === answerVariants.length) answerVariant.hidden = true

    return answerVariant
  })
})

// A matrix draws each variant as a batch, and every batch lists the same answer variants -- the
// ones the first variant's pattern resolves to.
const calcMxData = (answerVariants, subItemAnswerVariants) => ({
  batches: answerVariants.map((av, index) => ({
    ...withImage(av),
    answerVariants: subItemAnswerVariants,
    position: index + 1
  }))
})

// Anything else is drawn as plain answer variants. A variant the researcher can comment on says
// so, and a variant carrying a base of its own makes the totals and the respondent type moot.
const calcAvData = ({ answer, answerVariants, pattern, patternType }) => ({
  answerVariants: answerVariants.map(av => ({
    ...withImage(av),
    comments: undefined !== answer[`a${av.position}`]?.comments
  })),
  ignoreRType: answerVariants.some(av => 0 < av.base?.length),
  ignoreTotal: (0 < pattern.length && 'number' === patternType) || answerVariants.some(av => av.base?.length)
})

export const calcModelData = ({
  id,
  key,
  variant,
  answer,
  answerVariants,
  pattern,
  patternType,
  subItemAnswerVariants
}) => {
  // handed straight off the model, where an attribute that was cleared reads null rather than
  // absent -- a destructuring default answers only for the absent one, so they are settled here
  const variants = answerVariants || []
  const subItemVariants = subItemAnswerVariants || []
  const questionPattern = pattern || ''

  const data = {
    id,
    key,
    type: variant,
    answer: structuredClone(answer || {}),
    ignoreLogic: true,
    // the researcher's own construction rather than a question anybody was asked, so there is no
    // count of views to report under it however the variant draws it
    showTotals: false,
    // stated every time: the payload is set onto a model that may still be carrying the previous
    // variant's components, and only the extended open end restricts what its block draws
    components: null
  }

  if ('oex' === variant) return { ...data, ...calcOexData(variants) }
  if ('mx' === variant) return { ...data, ...calcMxData(variants, subItemVariants) }

  return {
    ...data,
    ...calcAvData({ answer: data.answer, answerVariants: variants, pattern: questionPattern, patternType })
  }
}

// The report params the drawn block owns, by name. The partial clears them while its answer is
// stale and puts them back once it is not, so what it has to remember is whatever the variant's
// block writes -- including the word cloud, which is addressed by question id rather than by key,
// and per answer variant everywhere except a plain open end.
export const calcLastParamNames = ({ id, key, type, answerVariants = [], batches = [] }) => {
  const names = [`s${key}`, `cb${key}`, `cm${key}`, `z${key}`]

  if ('oe' === type) return [...names, `oe${key}`, `wc${id}`]

  if ('mx' === type) {
    return [
      ...names,
      `tcv${key}`,
      ...batches.flatMap(batch => [
        `s${key}.${batch.position}`,
        `cm${key}.${batch.position}`,
        `z${key}.${batch.position}`
      ])
    ]
  }

  return [
    ...names,
    ...answerVariants.flatMap(av => [
      `s${key}.${av.position}`,
      `cm${key}.${av.position}`,
      `oe${key}.${av.position}`,
      `wc${id}-${av.position}`,
      `z${key}.${av.position}`
    ])
  ]
}
